import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import {
  NotificationService,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import { EMAIL_VERIFICATION_RESEND } from '../../constants/analytics-events';
import { UNKNOWN } from '../../constants/email-resend-source';
import { Segment } from '../../services/analytics-services/segment';
import Global from '../../store/global-selectors';
import { resendVerificationEmailInternal } from '../header-notification-banner/api';
import { SimpleModal } from '../simple-modal/simple-modal';

/**
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 */

/**
 * @param {object} props
 * @param {MessageDescriptor} [props.buttonText]
 * @param {() => void} props.closeUnverifiedModal
 * @param {() => void} [props.onSuccess]
 * @param {string} props.source
 * @returns {JSX.Element}
 */
export const VerifyEmailModal = ({ buttonText, closeUnverifiedModal, onSuccess = noop, source = UNKNOWN }) => {
  const sellerEmail = useSelector((state) => Global.selectSellerEmail(state));
  const sellerId = useSelector(Global.selectSellerSellerId);
  const onResendClick = useCallback(() => {
    Segment.trackEvent(EMAIL_VERIFICATION_RESEND, {
      sellerId,
      source,
      sellerEmail,
    });
    resendVerificationEmailInternal(sellerEmail)
      .then(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.emailResentTitle} />,
          content: <FormattedMessage {...messages.emailResentBody} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.emailResentErrorTitle} />,
          content: <FormattedMessage {...messages.emailResentErrorBody} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
      });
  }, [sellerEmail, sellerId, source]);

  const internalOnSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
    closeUnverifiedModal();
  }, [closeUnverifiedModal, onSuccess]);
  return (
    <SimpleModal
      actionMessage={buttonText || messages.action}
      headerMessage={messages.header}
      onAction={internalOnSuccess}
      onClose={closeUnverifiedModal}
      size='small'
    >
      <Text textType={TextType.Paragraph} value={messages.pleaseCheckEmail} />
      <br />
      <Text
        formattedMessageValues={{
          link: (chunks) => (
            <Button onClick={onResendClick} theme='link'>
              {chunks}
            </Button>
          ),
        }}
        textType={TextType.Paragraph}
        value={messages.contactSupport}
      />
    </SimpleModal>
  );
};
